/**
 * DataPage — Gegevens (spec §8.8, §11).
 *
 * (a) Export: repositories.profile.exportMyData() → JSON Blob download.
 * (b) Legacy import: detectLegacyLocalData() summary → preview counts →
 *     explicit confirm → importLegacyLocalData(onProgress) → honest result
 *     card (imported / skipped / backup key). The import is idempotent in the
 *     service layer (content hash + id mapping); re-running never duplicates.
 * (c) Delete account: strong ConfirmDestructiveAction →
 *     repositories.profile.deleteMyAccount(). A typed
 *     ACCOUNT_DELETION_UNAVAILABLE error shows the honest "serverfunctie
 *     ontbreekt" state with a pointer to Hulp — never a fake success.
 * (d) Storage/sync overview via the shared StorageStatusCard.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banner,
  Button,
  Card,
  ConfirmDestructiveAction,
  Dialog,
  ErrorState,
  Progress,
  StatusCard,
  useToast,
} from '@/components/ui'
import { PageHeader } from '@/components/navigation'
import { useAuth } from '@/features/auth'
import { repositories } from '@/services/repositories'
import {
  detectLegacyLocalData,
  importLegacyLocalData,
  LEGACY_BACKUP_KEY,
} from '@/services/sync'
import { isCloudConfigured } from '@/lib/supabase/client'
import { isAppError, toAppError } from '@/lib/supabase/errors'
import { PATHS } from '@/app/router/paths'
import type { AppError } from '@/types'
import type { LegacyImportProgress, LegacyImportResult, LegacyLocalSummary } from '@/services/sync'
import { StorageStatusCard } from './StorageStatusCard'
import './settings.css'

const LEGACY_LABELS: Record<keyof LegacyLocalSummary, string> = {
  plannerItems: 'Planneractiviteiten',
  notes: 'Notities (Onthouden)',
  places: 'Plaatsen',
  journalEntries: 'Dagboekregels',
  healthItems: 'Gezondheidsitems',
  breathingSessions: 'Ademhalingssessies',
}

function todayFileStamp(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

type DeleteState = 'idle' | 'deleting' | 'unavailable'

export default function DataPage() {
  const { signOut } = useAuth()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const cloud = isCloudConfigured()

  // Export
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<AppError | null>(null)

  // Legacy import
  const [legacySummary, setLegacySummary] = useState<LegacyLocalSummary | null>(null)
  const [legacyChecked, setLegacyChecked] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState<LegacyImportProgress | null>(null)
  const [importResult, setImportResult] = useState<LegacyImportResult | null>(null)
  const [importError, setImportError] = useState<AppError | null>(null)

  // Delete account
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteState, setDeleteState] = useState<DeleteState>('idle')
  const [deleteError, setDeleteError] = useState<AppError | null>(null)

  useEffect(() => {
    setLegacySummary(detectLegacyLocalData())
    setLegacyChecked(true)
  }, [])

  const legacyTotal = legacySummary
    ? Object.values(legacySummary).reduce((sum, count) => sum + count, 0)
    : 0

  const handleExport = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const blob = await repositories.profile.exportMyData()
      downloadBlob(blob, `rust-export-${todayFileStamp()}.json`)
      showToast({ message: 'Je gegevens zijn gedownload als JSON-bestand.', tone: 'success' })
    } catch (error) {
      setExportError(toAppError(error, 'database'))
    } finally {
      setExporting(false)
    }
  }

  const handleImport = async () => {
    setConfirmOpen(false)
    setImporting(true)
    setImportError(null)
    setImportResult(null)
    setProgress(null)
    try {
      const result = await importLegacyLocalData(setProgress)
      setImportResult(result)
      setLegacySummary(detectLegacyLocalData())
      showToast({ message: 'De import is afgerond.', tone: 'success' })
    } catch (error) {
      setImportError(toAppError(error, 'database'))
    } finally {
      setImporting(false)
      setProgress(null)
    }
  }

  const handleDelete = async () => {
    setDeleteOpen(false)
    setDeleteState('deleting')
    setDeleteError(null)
    try {
      await repositories.profile.deleteMyAccount()
      // The server confirmed deletion; the local session is now invalid.
      await signOut().catch(() => undefined)
      navigate(PATHS.welcome, { replace: true })
    } catch (error) {
      if (isAppError(error) && error.code === 'ACCOUNT_DELETION_UNAVAILABLE') {
        setDeleteState('unavailable')
      } else {
        setDeleteState('idle')
        setDeleteError(toAppError(error, 'database'))
      }
    }
  }

  return (
    <div className="settings-page">
      <PageHeader title="Gegevens" subtitle="Exporteren, importeren en verwijderen" backTo={PATHS.app.more} />

      {!cloud && (
        <Banner tone="warning">
          De cloudverbinding is niet ingesteld. Exporteren en accountverwijdering zijn nu niet
          beschikbaar; lokale concepten blijven op dit apparaat bewaard.
        </Banner>
      )}

      {/* (a) Export */}
      <section className="settings-section" aria-label="Gegevens exporteren">
        <h2 className="settings-section__title">Exporteren</h2>
        <Card>
          <div className="settings-stack">
            <p className="a11y-preview__body">
              Download al je gegevens (profiel, planner, notities, dagboek, gezondheid, plaatsen en
              ademhalingssessies) als één JSON-bestand. Het bestand bevat geen wachtwoorden of
              inloggegevens.
            </p>
            <Button onClick={() => void handleExport()} loading={exporting} disabled={!cloud} fullWidth>
              Gegevens exporteren
            </Button>
            {exportError && (
              <ErrorState error={exportError} onRetry={() => void handleExport()} />
            )}
          </div>
        </Card>
      </section>

      {/* (b) Legacy import */}
      <section className="settings-section" aria-label="Oude gegevens importeren">
        <h2 className="settings-section__title">Oude gegevens importeren</h2>
        {!legacyChecked ? null : legacySummary ? (
          <Card>
            <div className="settings-stack">
              <p className="a11y-preview__body">
                Er zijn gegevens van een eerdere versie van RUST op dit apparaat gevonden:
              </p>
              <ul className="legacy-summary">
                {(Object.keys(LEGACY_LABELS) as (keyof LegacyLocalSummary)[])
                  .filter((key) => legacySummary[key] > 0)
                  .map((key) => (
                    <li key={key} className="legacy-summary__row">
                      <span className="storage-status__label">{LEGACY_LABELS[key]}</span>
                      <span className="storage-status__value">{legacySummary[key]}</span>
                    </li>
                  ))}
              </ul>
              {importing && progress && (
                <Progress
                  value={progress.total > 0 ? progress.done / progress.total : 0}
                  aria-label={`Importeren: ${progress.done} van ${progress.total}`}
                />
              )}
              <Button
                onClick={() => setConfirmOpen(true)}
                loading={importing}
                disabled={!cloud}
                fullWidth
              >
                {importing ? 'Bezig met importeren…' : `${legacyTotal} ${legacyTotal === 1 ? 'item' : 'items'} importeren`}
              </Button>
              {importResult && (
                <StatusCard
                  status={importResult.imported > 0 ? 'success' : 'info'}
                  title={
                    importResult.imported > 0
                      ? `${importResult.imported} ${importResult.imported === 1 ? 'item' : 'items'} geïmporteerd`
                      : 'Alles was al eerder geïmporteerd'
                  }
                  body={`${importResult.skipped} overgeslagen. Er blijft een lokale back-up bewaard onder de sleutel ‘${importResult.backupKey}’; die kun je hierna zelf verwijderen uit de browseropslag.`}
                />
              )}
              {importError && (
                <ErrorState error={importError} onRetry={() => void handleImport()} />
              )}
            </div>
          </Card>
        ) : (
          <StatusCard
            status="info"
            title="Geen oude gegevens gevonden"
            body="Er is niets van een eerdere versie van RUST op dit apparaat aangetroffen."
          />
        )}
      </section>

      {/* (d) Storage & sync overview */}
      <section className="settings-section" aria-label="Opslag en synchronisatie">
        <h2 className="settings-section__title">Opslag en synchronisatie</h2>
        <StorageStatusCard />
      </section>

      {/* (c) Delete account */}
      <section className="settings-section" aria-label="Account verwijderen">
        <h2 className="settings-section__title">Account verwijderen</h2>
        {deleteState === 'unavailable' ? (
          <StatusCard
            status="error"
            title="Accountverwijdering vereist een serverfunctie"
            body="De benodigde serverfunctie is nu niet beschikbaar, dus je account is NIET verwijderd. Neem contact op via Hulp om je account te laten verwijderen."
            action={
              <Button variant="secondary" onClick={() => navigate(PATHS.legal.help)}>
                Naar Hulp
              </Button>
            }
          />
        ) : (
          <Card>
            <div className="settings-stack">
              <p className="a11y-preview__body">
                Dit verwijdert je account en al je cloudgegevens definitief. Dit kan niet ongedaan
                worden gemaakt. Exporteer eerst je gegevens als je ze wilt bewaren.
              </p>
              <Button
                variant="danger"
                onClick={() => setDeleteOpen(true)}
                loading={deleteState === 'deleting'}
                disabled={!cloud}
                fullWidth
              >
                Account verwijderen
              </Button>
              {deleteError && <ErrorState error={deleteError} onRetry={() => void handleDelete()} />}
            </div>
          </Card>
        )}
      </section>

      {/* Import confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Oude gegevens importeren?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={() => void handleImport()}>Importeren</Button>
          </>
        }
      >
        <p className="about-dialog__body">
          Er worden {legacyTotal} {legacyTotal === 1 ? 'item' : 'items'} naar je account gekopieerd.
          Voordat de import start wordt een lokale back-up gemaakt (‘{LEGACY_BACKUP_KEY}’). De
          import is eenmalig per item: opnieuw uitvoeren importeert niets dubbel. Je oude lokale
          gegevens blijven ongewijzigd bewaard.
        </p>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDestructiveAction
        open={deleteOpen}
        title="Account definitief verwijderen?"
        body="Dit verwijdert je account en al je cloudgegevens permanent: profiel, planner, notities, dagboek, gezondheidsgegevens, plaatsen en ademhalingssessies. Dit kan NIET ongedaan worden gemaakt. Je moet recent zijn ingelogd om dit te mogen doen."
        confirmLabel="Definitief verwijderen"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}
